import { useAppSelector } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';

import Divider from 'flow/components/ui-components/Divider';
import RouteLink from 'flow/components/ui-components/RouteLink';
import useAppContext from 'flow/hooks/useAppContext';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { selectSoftphoneIsRegistered } from 'flow/selectors/call-selectors';
import { getCallMethod } from 'flow/selectors/selectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';

import {
    UserAppSettingOption,
    getCallMethodFromSetting,
    isSettingAppliedEverywhere,
    useIncomingCallSettings,
    useOutgoingCallSettings,
    useOutgoingCallsEnabled,
} from 'flow/hooks/api/useSoftphoneCallSettings';
import { isAppCti, isTeamsTypeIntegration } from 'flow/utils/app-utils';
import { isChromeOrElectron } from 'flow/utils/browser-utils';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import { PlayRingtoneSetting } from 'flow/utils/call-utils';
import { generatePath } from 'flow/utils/router-utils';
import HandoverToDesktopSetting from 'flow/companion/HandoverToDesktopSetting';
import Page from './Page';
import HeadsetControls from './Softphone/HeadsetControls';
import VideoMeetingAsSIP from './Softphone/VideoMeetingAsSIP';
const Softphone = () => {
    const { i18next, embedded, phoneEnabled } = useAppContext();
    const history = useHistory();
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const { isSinglePaneMode } = useSinglePaneMode();

    const callMethod = useAppSelector(getCallMethod);

    const isRegistered = useAppSelector(selectSoftphoneIsRegistered);

    const outgoingCallsEnabled = useOutgoingCallsEnabled();
    const outgoingCallsDisabled = !outgoingCallsEnabled;

    const { data: incomingSettings } = useIncomingCallSettings();
    const incomingSettingOption = incomingSettings?.value;
    const incomingEnabled =
        incomingSettingOption === UserAppSettingOption.ON
            ? true
            : incomingSettingOption === UserAppSettingOption.OFF
              ? false
              : isRegistered;

    const { data: outgoingSettings } = useOutgoingCallSettings();
    const outgoingSettingOption = outgoingSettings?.value;
    const effectiveCallMethod = isSettingAppliedEverywhere(
        outgoingSettingOption,
    )
        ? (getCallMethodFromSetting(outgoingSettingOption) ?? callMethod)
        : callMethod;

    const playRingtoneSetting = useAppSelector((state) =>
        PlayRingtoneSetting.get(state.phone.get('playRingtoneSetting')),
    );

    const isAppCtiResult = useAppSelector(isAppCti);

    const showSoftphoneSettings =
        (isChromeOrElectron() || isAppCtiResult) &&
        !isTeamsTypeIntegration(embedded) &&
        phoneEnabled;

    return (
        <Page
            header="settings.softphone.telephony_and_audio"
            onClickBack={
                isSinglePaneMode
                    ? () =>
                          history.push(
                              generatePath(routes['settings'], {
                                  extensionKey,
                              }),
                          )
                    : undefined
            }
        >
            {showSoftphoneSettings && (
                <RouteLink
                    to={routes['settings.softphone.incoming-calls']}
                    description={i18next.t(
                        incomingEnabled
                            ? 'settings.softphone.enabled'
                            : 'settings.softphone.disabled',
                    )}
                    className="py-4"
                >
                    {i18next.t('settings.softphone.incoming_calls')}
                </RouteLink>
            )}

            {showSoftphoneSettings && (
                <RouteLink
                    to={routes['settings.softphone.outgoing-calls']}
                    description={i18next.t(
                        outgoingCallsDisabled
                            ? 'settings.softphone.outgoing_calls_disabled'
                            : `settings.callmethod.${effectiveCallMethod.value}`,
                    )}
                    className="py-4"
                >
                    {i18next.t('settings.softphone.outgoing_calls')}
                </RouteLink>
            )}

            {showSoftphoneSettings && (
                <>
                    <RouteLink
                        to={routes['settings.softphone.ringtone']}
                        description={i18next.t(
                            `settings.softphone.${playRingtoneSetting.value}`,
                        )}
                        className="py-4"
                    >
                        {i18next.t('media.settings.ringtonevolume')}
                    </RouteLink>

                    <Divider />

                    <HeadsetControls />

                    <Divider />

                    <RouteLink
                        to={routes['settings.softphone.audio-in-out']}
                        className="py-4"
                    >
                        {i18next.t('settings.softphone.audio_device')}
                    </RouteLink>

                    <Divider />
                </>
            )}

            <RouteLink
                to={routes['settings.softphone.advanced']}
                className="py-4"
            >
                {i18next.t('settings.callrouting')}
            </RouteLink>

            <Divider />
            <HandoverToDesktopSetting />

            {!IS_UAE_HOST && !isTeamsTypeIntegration(embedded) && (
                <>
                    <Divider />
                    <VideoMeetingAsSIP />
                </>
            )}
            {isRegistered && (
                <div
                    data-test-id="softphone-is-registered"
                    className="hidden"
                />
            )}
        </Page>
    );
};

export default Softphone;
